import { CREATURE_BY_ID, GLYPHS, PALETTE } from "@eldrun/content";
import type { MatchStateType } from "@eldrun/protocol";
import { Vector3, type Camera } from "three";
import { gameColor, playerBySlot, playerColor } from "../../render/style/roles.ts";
import { edgeArrow } from "../../ui/hud/offscreen-model.ts";
import { OffscreenArrows, type ArrowTarget } from "../../ui/hud/offscreen-arrows.ts";
import { PartyFrames } from "../../ui/hud/party-frames.ts";
import { partyView } from "../../ui/hud/party-model.ts";
import { renderStatusChips, statusList } from "../../ui/hud/status-glyphs.ts";
import { h } from "../../ui/kit/dom.ts";

/** Altura do ponto mirado acima do pé (o peito), para a projeção das setas. */
const CHEST = 1.2;

/**
 * HUD do grupo (F63; R2: código novo fora da `GameScene`): quadros do grupo a 2 Hz e setas fora da
 * tela por quadro. Lê só o estado público e a câmera; a cena chama `update` no laço.
 */
export class GroupHud {
  private readonly frames: PartyFrames;
  private readonly arrows: OffscreenArrows;
  /** Os meus status (F76), sob o cartão do jogador (F109), na cor de quem aplicou. */
  private readonly mine: HTMLElement;
  private readonly scratch = new Vector3();
  private nextFramesAt = 0;
  /** Status dos aliados no último quadro (F76): mudou, os quadros redesenham na hora (o piscar não atrasa). */
  private statusKey = "";

  /** `framesParent`: onde ficam os quadros e os meus status (F81, F109: a coluna da esquerda do HUD). */
  constructor(parent: HTMLElement, framesParent: HTMLElement = parent) {
    this.frames = new PartyFrames(framesParent);
    this.arrows = new OffscreenArrows(parent);
    this.mine = h("div", { id: "status-proprio", "aria-label": "Seus status" });
    framesParent.appendChild(this.mine);
  }

  update(state: MatchStateType, me: string, camera: Camera, width: number, height: number, now: number): void {
    let key = "";
    for (const [id, v] of state.vitals) if (id !== me) key += `${id}:${v.status}:${v.statusEnding};`;
    const changed = key !== this.statusKey;
    this.statusKey = key;
    if (now >= this.nextFramesAt || changed) {
      this.nextFramesAt = now + 500;
      this.frames.update(
        partyView(
          { players: state.players.entries(), vitals: (id) => state.vitals.get(id), position: (id) => state.avatars.get(id) },
          me,
          gameColor,
        ),
      );
    }
    const v = state.vitals.get(me);
    // A borda na cor da classe de quem aplicou (F85; o `statusBy` é o número dele); sem ninguém, a minha.
    const source = v && v.statusBy > 0 ? playerBySlot(state.players.values(), v.statusBy - 1) : state.players.get(me);
    this.mine.style.setProperty("--fonte", playerColor(source));
    renderStatusChips(this.mine, statusList(v?.status ?? 0, v?.statusEnding ?? 0));
    const targets: ArrowTarget[] = [];
    const project = (x: number, y: number, z: number) => {
      const p = this.scratch.set(x, y, z).applyMatrix4(camera.matrixWorldInverse);
      const behind = p.z > 0;
      p.applyMatrix4(camera.projectionMatrix);
      return edgeArrow(p.x, p.y, behind, width, height);
    };
    for (const [id, p] of state.players.entries()) {
      if (id === me) continue;
      const a = state.avatars.get(id);
      if (!a || state.vitals.get(id)?.dead) continue;
      const arrow = project(a.x, a.y + CHEST, a.z);
      if (arrow) targets.push({ id, kind: "aliado", label: String(p.slot + 1), color: playerColor(p), ...arrow });
    }
    for (const [id] of state.bosses?.entries() ?? []) {
      const c = state.creatures.get(id);
      if (!c || c.hp <= 0) continue;
      const tall = CREATURE_BY_ID.get(c.type)?.radius ?? 1;
      const arrow = project(c.x, c.y + tall * 1.5, c.z);
      if (arrow) targets.push({ id, kind: "chefe", label: GLYPHS.coroa.char, color: gameColor(PALETTE.chefe.hex), ...arrow });
    }
    this.arrows.update(targets);
  }

  /** Sai da partida: tira os quadros, as setas e os status do DOM. */
  remove(): void {
    this.frames.element.remove();
    this.arrows.element.remove();
    this.mine.remove();
  }
}
